import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Textarea } from '../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Database,
  Search,
  Upload,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2,
  Phone,
  Plus,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';

const R2GDashboardPage = () => {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [stats, setStats] = useState({
    r2g_contacts: 0,
    servall_contacts: 0,
    total_database: 0,
    recent_entries: []
  });
  
  // Input state
  const [mobileInput, setMobileInput] = useState('');
  const [checkResults, setCheckResults] = useState(null);
  const [selectedNumbers, setSelectedNumbers] = useState([]);
  const fileInputRef = useRef(null);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/r2g/stats');
      setStats(response.data);
    } catch (err) {
      toast.error('Failed to load R2G stats');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleCheckDuplicates = async () => {
    if (!mobileInput.trim()) {
      toast.error('Please enter mobile numbers');
      return;
    }

    // Parse mobile numbers (comma, newline, or space separated)
    const numbers = mobileInput
      .split(/[\n,\s]+/)
      .map(n => n.trim())
      .filter(n => n.length > 0);

    if (numbers.length === 0) {
      toast.error('No valid numbers found');
      return;
    }

    setChecking(true);
    try {
      const response = await api.post('/r2g/check-duplicates', numbers);
      setCheckResults(response.data);
      setSelectedNumbers(
        response.data.results
          .filter(r => r.canAssign)
          .map(r => r.normalized)
      );
      toast.success(`Checked ${numbers.length} numbers`);
    } catch (err) {
      toast.error('Failed to check duplicates');
    } finally {
      setChecking(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv') && !file.name.endsWith('.xlsx')) {
      toast.error('Please upload a CSV file');
      return;
    }

    setChecking(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/r2g/upload-csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setCheckResults(response.data);
      setSelectedNumbers(
        response.data.results
          .filter(r => r.canAssign)
          .map(r => r.normalized)
      );
      toast.success(`Checked ${response.data.summary.total} numbers from file`);
    } catch (err) {
      toast.error('Failed to process file');
    } finally {
      setChecking(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleToggleSelect = (normalized) => {
    setSelectedNumbers(prev => 
      prev.includes(normalized)
        ? prev.filter(n => n !== normalized)
        : [...prev, normalized]
    );
  };

  const handleSelectAllUnique = () => {
    if (!checkResults) return;
    const uniqueNumbers = checkResults.results
      .filter(r => r.canAssign)
      .map(r => r.normalized);
    setSelectedNumbers(uniqueNumbers);
  };

  const handleAssignToR2G = async () => {
    if (selectedNumbers.length === 0) {
      toast.error('No numbers selected');
      return;
    }

    setAssigning(true);
    try {
      const response = await api.post('/r2g/assign', selectedNumbers);
      toast.success(`Assigned ${response.data.assigned} numbers to R2G`);
      
      // Refresh stats and clear results
      await fetchStats();
      setCheckResults(null);
      setSelectedNumbers([]);
      setMobileInput('');
    } catch (err) {
      toast.error('Failed to assign numbers');
    } finally {
      setAssigning(false);
    }
  };

  const getStatusBadge = (status, source) => {
    switch (status) {
      case 'Unique':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle2 className="h-3 w-3 mr-1" />Unique</Badge>;
      case 'Duplicate':
        return <Badge className="bg-red-100 text-red-800"><XCircle className="h-3 w-3 mr-1" />Duplicate ({source})</Badge>;
      case 'Invalid':
        return <Badge className="bg-yellow-100 text-yellow-800"><AlertCircle className="h-3 w-3 mr-1" />Invalid</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="r2g-loading">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="r2g-dashboard">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight flex items-center gap-2">
          <Database className="h-8 w-8 text-primary" />
          R2G Duplicate Check
        </h1>
        <p className="text-muted-foreground">
          Manual duplicate checking against Servall and R2G databases
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">R2G Contacts</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-primary">{stats.r2g_contacts.toLocaleString()}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Servall Contacts</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">{stats.servall_contacts.toLocaleString()}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Database</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats.total_database.toLocaleString()}</div>
          </CardContent>
        </Card>
      </div>

      {/* Duplicate Check Section */}
      <Card>
        <CardHeader>
          <CardTitle>Check for Duplicates</CardTitle>
          <CardDescription>
            Enter mobile numbers or upload a CSV file to check against R2G and Servall databases
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Manual Input */}
            <div className="space-y-2">
              <Label>Paste Mobile Numbers</Label>
              <Textarea
                placeholder="Enter mobile numbers (one per line or comma separated)&#10;Example:&#10;9876543210&#10;9123456789&#10;8765432109"
                value={mobileInput}
                onChange={(e) => setMobileInput(e.target.value)}
                className="h-32 font-mono text-sm"
                data-testid="mobile-input"
              />
              <Button 
                onClick={handleCheckDuplicates} 
                disabled={checking || !mobileInput.trim()}
                className="w-full"
                data-testid="check-btn"
              >
                {checking ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Search className="mr-2 h-4 w-4" />
                )}
                Check Duplicates
              </Button>
            </div>

            {/* CSV Upload */}
            <div className="space-y-2">
              <Label>Or Upload CSV File</Label>
              <div className="border-2 border-dashed rounded-lg p-6 text-center">
                <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground mb-2">
                  CSV with column: mobile, phone, or mobile_number
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx"
                  onChange={handleFileUpload}
                  className="hidden"
                  data-testid="csv-input"
                />
                <Button 
                  variant="outline" 
                  onClick={() => fileInputRef.current?.click()}
                  disabled={checking}
                >
                  {checking ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="mr-2 h-4 w-4" />
                  )}
                  Select CSV File
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results Section */}
      {checkResults && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Check Results</CardTitle>
                <CardDescription>
                  {checkResults.summary.unique} Unique | {checkResults.summary.duplicate} Duplicate | {checkResults.summary.invalid} Invalid
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleSelectAllUnique}
                  disabled={checkResults.summary.unique === 0}
                >
                  Select All Unique
                </Button>
                <Button
                  onClick={handleAssignToR2G}
                  disabled={assigning || selectedNumbers.length === 0}
                  data-testid="assign-btn"
                >
                  {assigning ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="mr-2 h-4 w-4" />
                  )}
                  Assign to R2G ({selectedNumbers.length})
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="max-h-96 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">Select</TableHead>
                    <TableHead>Mobile Number</TableHead>
                    <TableHead>Normalized</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {checkResults.results.map((result, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        {result.canAssign && (
                          <input
                            type="checkbox"
                            checked={selectedNumbers.includes(result.normalized)}
                            onChange={() => handleToggleSelect(result.normalized)}
                            className="h-4 w-4 rounded border-gray-300"
                          />
                        )}
                      </TableCell>
                      <TableCell className="font-mono">{result.mobile}</TableCell>
                      <TableCell className="font-mono text-muted-foreground">{result.normalized}</TableCell>
                      <TableCell>{getStatusBadge(result.status, result.source)}</TableCell>
                      <TableCell>
                        {result.canAssign ? (
                          <span className="text-sm text-green-600">Can Assign</span>
                        ) : (
                          <span className="text-sm text-muted-foreground">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Entries */}
      <Card>
        <CardHeader>
          <CardTitle>Recent R2G Entries</CardTitle>
          <CardDescription>Last 50 numbers added to R2G database</CardDescription>
        </CardHeader>
        <CardContent>
          {stats.recent_entries.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Database className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No entries yet</p>
            </div>
          ) : (
            <div className="max-h-64 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Mobile</TableHead>
                    <TableHead>Added By</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Source</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stats.recent_entries.map((entry, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-mono">
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-muted-foreground" />
                          {entry.mobile}
                        </div>
                      </TableCell>
                      <TableCell>{entry.added_by}</TableCell>
                      <TableCell>
                        {entry.added_at ? new Date(entry.added_at).toLocaleDateString() : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{entry.source}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Important Note */}
      <Card className="border-orange-200 bg-orange-50/30">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-orange-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-orange-800">Important: R2G Mode</h4>
              <p className="text-sm text-orange-700">
                R2G is for duplicate checking only. Numbers assigned here are saved to the R2G database 
                and will be used for future duplicate checks. <strong>No CRM leads are created from this flow.</strong>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default R2GDashboardPage;
